import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@spz/ui/components/popover";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatNumberRange } from "../lib/format";
import { Alert, AlertDescription } from "./alert";
import { Button } from "./button";
import { TextField } from "./textfield";
import { Typography } from "./typography";

type NumberFilterProps = {
  min?: number;
  minLimit?: number;
  max?: number;
  maxLimit?: number;
  onApply: (min?: number, max?: number) => void;
  onReset: () => void;
  error?: string;
  label: string;
  disabled?: boolean;
};

const NumberFilter: FC<NumberFilterProps> = (props) => {
  const [isOpen, setIsOpen] = useState(false);

  const [min, setMin] = useState<number | undefined>(props.min);
  const [max, setMax] = useState<number | undefined>(props.max);

  const onApply = useCallback(
    (newMin: number | undefined, newMax: number | undefined) => {
      setMin(newMin);
      setMax(newMax);
      props.onApply(newMin, newMax);
      setIsOpen(false);
    },
    [props],
  );

  const onReset = useCallback(() => {
    setMin(undefined);
    setMax(undefined);
    props.onReset();
  }, [props]);

  useEffect(() => {
    setMin(props.min);
    setMax(props.max);
  }, [props.min, props.max]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger disabled={props.disabled}>
        <NumberFilterTrigger
          isOpen={isOpen}
          label={props.label}
          min={min}
          max={max}
        />
      </PopoverTrigger>
      <PopoverContent className="p-2">
        <NumberFilterContent
          {...props}
          min={min}
          max={max}
          onApply={onApply}
          onReset={onReset}
        />
      </PopoverContent>
    </Popover>
  );
};

const NumberFilterTrigger: FC<{
  isOpen: boolean;
  label: string;
  min?: number;
  max?: number;
}> = ({ isOpen, label, min, max }) => {
  const buttonText = useMemo(() => {
    if (min === undefined && max === undefined) {
      return label;
    }

    const display = formatNumberRange(min, max);

    return (
      <>
        {label}
        <Typography className="text-gray-700 font-normal">{display}</Typography>
      </>
    );
  }, [label, min, max]);

  return (
    <Button
      asChild
      variant="ghost"
      rightIcon={isOpen ? "chevron-up" : "chevron-down"}
      className={isOpen ? "bg-gray-100" : ""}
    >
      {buttonText}
    </Button>
  );
};

const NumberFilterContent: FC<NumberFilterProps> = ({
  min,
  minLimit,
  max,
  maxLimit,
  onApply,
  onReset,
  error = "",
}) => {
  const { t } = useTranslation();

  const [localMin, setLocalMin] = useState<number | undefined>(min);
  const [localMax, setLocalMax] = useState<number | undefined>(max);
  const [localError, setLocalError] = useState<string>(error);
  const [dirty, setDirty] = useState(false);

  // Sync error from parent unless user has modified since last apply
  useEffect(() => {
    if (!dirty) {
      setLocalError(error);
    }
  }, [error, dirty]);

  // Keep local values in sync with props
  useEffect(() => {
    setLocalMin(min);
    setLocalMax(max);
  }, [min, max]);

  const handleApply = useCallback(() => {
    setDirty(false);
    onApply(localMin, localMax);
  }, [localMin, localMax, onApply]);

  const handleReset = useCallback(() => {
    setLocalMin(undefined);
    setLocalMax(undefined);
    setLocalError("");
    setDirty(false);
    onReset();
  }, [onReset]);

  const handleMinChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDirty(true);
      setLocalError("");

      const min = e.target.value ? Number(e.target.value) : undefined;

      if (min !== undefined && localMax !== undefined && min > localMax) {
        setLocalError(
          t("minimum_value_must_be_less_than_or_equal_maximum_value"),
        );
      }

      setLocalMin(min ?? undefined);
    },
    [localMax, t],
  );

  const handleMaxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDirty(true);
      setLocalError("");

      const max = e.target.value ? Number(e.target.value) : undefined;

      if (localMin !== undefined && max !== undefined && localMin > max) {
        setLocalError(
          t("minimum_value_must_be_less_than_or_equal_maximum_value"),
        );
      }

      setLocalMax(max ?? undefined);
    },
    [localMin, t],
  );

  return (
    <div className="flex flex-col gap-2">
      {localError && (
        <Alert variant="critical">
          <AlertDescription>{localError}</AlertDescription>
        </Alert>
      )}

      <div className="flex gap-2 font-semibold text-gray-800">
        <div className="w-full">
          <Typography>{t("minimum")}</Typography>
          <TextField
            type="number"
            value={localMin ?? ""}
            min={minLimit}
            aria-label={t("minimum")}
            onChange={handleMinChange}
          />
        </div>
        <div className="w-full">
          <Typography>{t("maximum")}</Typography>
          <TextField
            type="number"
            value={localMax ?? ""}
            max={maxLimit}
            aria-label={t("maximum")}
            onChange={handleMaxChange}
          />
        </div>
      </div>

      <div className="flex gap-2">
        <Button onClick={handleReset} variant="secondary" className="w-full">
          {t("reset")}
        </Button>
        <Button
          onClick={handleApply}
          className="w-full"
          disabled={!!localError}
        >
          {t("apply")}
        </Button>
      </div>
    </div>
  );
};

export { NumberFilter };
